import { useId, useState, type FormEvent } from "react";
import type { SplitRowInput } from "@/api/finance";
import { RequestError } from "@/components/RequestError";
import { FormActions } from "@/components/ui/form-actions";
import { Input } from "@/components/ui/input";
import { useSubmitState } from "@/lib/use-form";

/** One bucket the invoice can give a share to; `percent` is "" when it gets none. */
export type SplitDraft = SplitRowInput & { bucket_name: string };

type SplitEditorProps = {
  rows: SplitDraft[];
  onSave: (rows: SplitRowInput[]) => Promise<unknown>;
  onCancel: () => void;
};

/** Hundredths of a percent, so 33.33 + 66.67 is exactly 100. */
function hundredths(percent: string): number {
  const value = Math.round(Number(percent) * 100);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function SplitEditor({ rows, onSave, onCancel }: SplitEditorProps) {
  const id = useId();
  const [drafts, setDrafts] = useState(rows);
  const { error, saving, run } = useSubmitState();
  const total = drafts.reduce((sum, row) => sum + hundredths(row.percent), 0);
  const whole = total === 10000;

  function change(index: number, field: "percent" | "payee", value: string): void {
    setDrafts((current) =>
      current.map((row, at) => (at === index ? { ...row, [field]: value } : row)),
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSave(
        drafts
          .filter((row) => hundredths(row.percent) > 0)
          .map((row) => ({
            bucket_id: row.bucket_id,
            percent: row.percent.trim(),
            payee: row.payee.trim(),
          })),
      ),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
      <RequestError error={error} />
      <div className="grid grid-cols-[minmax(0,1fr)_5.5rem] gap-x-3 gap-y-2 sm:grid-cols-[minmax(0,1fr)_5.5rem_minmax(0,1fr)]">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Bucket</span>
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Share %</span>
        <span className="hidden text-xs uppercase tracking-wide text-muted-foreground sm:block">
          Paid to
        </span>
        {drafts.map((row, index) => (
          <div key={row.bucket_id} className="contents">
            <span className="self-center text-sm font-medium">{row.bucket_name}</span>
            <div>
              <label htmlFor={`${id}-${index}-percent`} className="sr-only">
                {`${row.bucket_name} %`}
              </label>
              <Input
                id={`${id}-${index}-percent`}
                inputMode="decimal"
                value={row.percent}
                onChange={(event) => change(index, "percent", event.target.value)}
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor={`${id}-${index}-payee`} className="sr-only">
                {`${row.bucket_name} payee`}
              </label>
              <Input
                id={`${id}-${index}-payee`}
                value={row.payee}
                placeholder="Paid to (optional)"
                onChange={(event) => change(index, "payee", event.target.value)}
              />
            </div>
          </div>
        ))}
      </div>
      <p className={whole ? "text-sm" : "text-sm font-medium text-destructive"} role="status">
        {whole ? "Total 100%" : `Total ${total / 100}% - it must be 100%`}
      </p>
      <FormActions
        saving={saving}
        submitLabel="Save split"
        onCancel={onCancel}
        disabled={!whole}
      />
    </form>
  );
}
